/**
 * Super Admin — Broadcast (Compose + Log).
 */
import { useEffect, useState, useCallback } from "react";
import { Send } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";
import { SectionHeader } from "./SuperadminApp";
import { Card, TableShell, Th, Td, Modal, PrimaryBtn, GhostBtn, inputStyle, fieldLabel, EmptyState, StatusBadge, relTime } from "./_atoms";

const TARGETS = [
  { value: "all", label: "All Users" },
  { value: "city", label: "Users in city" },
  { value: "professionals", label: "All Professionals" },
  { value: "trainers", label: "Verified Trainers" },
  { value: "nutritionists", label: "Verified Nutritionists" },
  { value: "active", label: "Active in last N days" },
  { value: "streak", label: "Workout streak ≥ N days" },
  { value: "custom", label: "Custom user IDs" },
];

export function BroadcastCompose() {
  const [f, setF] = useState({ title: "", message: "", target: "all", target_value: "", user_ids: "", schedule_at: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const buildBody = () => ({
    title: f.title, message: f.message,
    target: f.target,
    target_value: f.target_value || null,
    user_ids: f.user_ids ? f.user_ids.split(/[\s,]+/).filter(Boolean) : [],
    schedule_at: f.schedule_at || null,
  });

  const dryRun = async () => {
    try {
      const { data } = await apiClient.post("/superadmin/broadcast/preview", buildBody());
      setPreview(data?.estimated_recipients ?? 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const send = async () => {
    if (!f.title || !f.message) return toast.error("Title & message required");
    if (preview == null) return toast.error("Run preview first");
    if (!window.confirm(`Send to ~${preview} recipients?`)) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post("/superadmin/broadcast/send", buildBody());
      toast.success(`Sent to ${data.recipients} recipients`);
      setF({ title: "", message: "", target: "all", target_value: "", user_ids: "", schedule_at: "" });
      setPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="broadcast-compose">
      <SectionHeader title="Compose Broadcast" />
      <Card>
        <div style={{ display: "grid", gap: 12 }}>
          <div><label style={fieldLabel}>Title (≤60)</label><input maxLength={60} style={inputStyle} value={f.title} onChange={(e) => { setF({ ...f, title: e.target.value }); setPreview(null); }} data-testid="b-title" /></div>
          <div><label style={fieldLabel}>Message (≤140)</label><textarea maxLength={140} value={f.message} onChange={(e) => { setF({ ...f, message: e.target.value }); setPreview(null); }} style={{ ...inputStyle, height: 90, padding: "10px 14px" }} data-testid="b-message" /></div>
          <div><label style={fieldLabel}>Target</label>
            <select value={f.target} onChange={(e) => { setF({ ...f, target: e.target.value }); setPreview(null); }} style={inputStyle} data-testid="b-target">
              {TARGETS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {(f.target === "city" || f.target === "active" || f.target === "streak") && (
            <div><label style={fieldLabel}>{f.target === "city" ? "City name" : f.target === "active" ? "Days" : "Min streak (days)"}</label>
              <input style={inputStyle} value={f.target_value} onChange={(e) => { setF({ ...f, target_value: e.target.value }); setPreview(null); }} data-testid="b-target-value" />
            </div>
          )}
          {f.target === "custom" && (
            <div><label style={fieldLabel}>User IDs (comma or newline)</label>
              <textarea value={f.user_ids} onChange={(e) => { setF({ ...f, user_ids: e.target.value }); setPreview(null); }} style={{ ...inputStyle, height: 80, padding: "10px 14px" }} />
            </div>
          )}
          <div><label style={fieldLabel}>Schedule at (ISO, optional)</label>
            <input style={inputStyle} value={f.schedule_at} onChange={(e) => setF({ ...f, schedule_at: e.target.value })} placeholder="2026-02-10T09:00:00Z" />
          </div>

          <div style={{ background: "var(--bg-inset, #D8D6D1)", padding: 16, borderRadius: 12 }}>
            <strong>Preview · {f.target.toUpperCase()}</strong>
            {f.title && <div style={{ marginTop: 6, fontWeight: 700 }}>{f.title}</div>}
            {f.message && <div style={{ fontSize: 13 }}>{f.message}</div>}
            <div style={{ marginTop: 8, fontSize: 12, color: "var(--text-tertiary, #8A8884)" }}>
              {preview == null ? "Run preview to estimate recipients" : `~${preview} recipients`}
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-tertiary, #8A8884)" }}>
              Limits: 3 broadcasts / 24h per admin · 1 per target / 4h · duplicate-detection on (title + target).
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <GhostBtn onClick={dryRun} data-testid="b-preview">Preview</GhostBtn>
            <PrimaryBtn onClick={send} disabled={busy || preview == null} data-testid="b-send"><Send size={14} /> {busy ? "Sending…" : "Send"}</PrimaryBtn>
          </div>
        </div>
      </Card>
    </div>
  );
}

export function BroadcastLog() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    apiClient.get("/superadmin/broadcast/log").then((r) => setRows(r.data?.broadcasts || []));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="broadcast-log">
      <SectionHeader title="Broadcast Log" onRefresh={load} />
      {rows.length === 0 ? <EmptyState title="No broadcasts yet" /> : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>Title</Th><Th>Target</Th><Th>Recipients</Th><Th>Delivered</Th><Th>Status</Th><Th>Sent</Th></tr></thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.broadcast_id}>
                  <Td><strong>{b.title}</strong><div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)" }}>{b.message?.slice(0, 80)}</div></Td>
                  <Td>{b.target}{b.target_value ? `:${b.target_value}` : ""}</Td>
                  <Td>{b.recipients_count}</Td>
                  <Td>{b.delivery_count || 0}</Td>
                  <Td><StatusBadge value={b.status} /></Td>
                  <Td>{relTime(b.created_at)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
    </div>
  );
}
